import React, { useEffect, useMemo, useState } from "react";
import { Button, Drawer, Form, Input, Modal, Select } from "antd";
import Icon from "@mdi/react";
import { mdiAccount, mdiAirplaneLanding, mdiAirplaneTakeoff, mdiBedDouble, mdiCalendarMonth, mdiCheckCircle, mdiChevronDown, mdiClose, mdiTranslate } from "@mdi/js";
import dayjs from "dayjs";
import useBreakpoint from "../../../../utils/useBreakpoint";
import ListApi from "../../../../api-services/list-api.service";
import { LandTourDetail } from "../../../../interfaces/land-tour.interface";
import { currencyDisplay } from "../../../../utils/helpers";
import TermsConsentCheckBox from "../../b2c2b/TermsConsentCheckBox";
import PrivacyConsentCheckbox from "../../b2c2b/PrivacyConsentCheckbox";
import { PAYMENT_CHANNELS } from "../../b2c2b/payment/PaymentMethodPanel";

export interface InvoiceAddress {
  address_1: string;
  address_2: string;
  city_name: string;
  state: string;
  country: string;
  postal_code: string;
}

export interface PaymentSummary {
  departureDate: string;
  travellers: string;
  partner?: string;
  room?: string;
  language?: string;
  outbound: { date?: string; line: string };
  inbound: { date?: string; line: string };
  onEdit: () => void;
}

interface Props {
  /** Mobile: the compact booking summary shown above the payment details. */
  summary?: PaymentSummary;
  landTour: LandTourDetail;
  total: number | undefined;
  address: InvoiceAddress;
  onAddressChange: (address: InvoiceAddress) => void;
  countries: { value: string; label: string }[];
  states: { value: string; label: string }[];
  paymentMethods: Record<string, any>;
  gateway: string;
  onGatewayChange: (gateway: string) => void;
  bank: string;
  onBankChange: (bank: string) => void;
  termsAgreed: boolean;
  onTermsChange: (value: boolean) => void;
  privacyAgreed: boolean;
  onPrivacyChange: (value: boolean) => void;
}

export const normaliseBankOptions = (list: any[]) =>
  list.map((item: any) => Array.isArray(item)
    ? { label: item[0], value: item[1], redirect_url: item[2] || "" }
    : typeof item === "string" ? { label: item, value: item, redirect_url: "" } : item);

const emptyAddressValues: InvoiceAddress = { address_1: "", address_2: "", city_name: "", state: "", country: "", postal_code: "" };

const ADDRESS_FIELDS: { key: keyof InvoiceAddress; label: string }[] = [
  { key: "address_1", label: "Address 1" },
  { key: "address_2", label: "Address 2" },
  { key: "state", label: "State" },
  { key: "city_name", label: "City" },
  { key: "postal_code", label: "Postcode" },
  { key: "country", label: "Country" },
];

interface BankOption { label: string; value: string; redirect_url?: string }

// "Choose your bank" (Figma 8821:24935 desktop modal, 10308:317416 mobile sheet): a radio list, not a dropdown.
function BankChooser({ open, title, options, value, mobile, onClose, onChange }: {
  open: boolean; title: string; options: BankOption[]; value: string; mobile: boolean; onClose: () => void; onChange: (value: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  const list = (
    <div className="gt-bank-list" role="radiogroup" aria-label={title}>
      {options.map((option) => {
        const checked = (mobile ? draft : value) === option.value;
        return (
          <button
            type="button" role="radio" aria-checked={checked} key={option.value} className={checked ? "is-selected" : ""}
            onClick={() => { if (mobile) setDraft(option.value); else { onChange(option.value); onClose(); } }}
          >
            <span>{option.label}</span><i />
          </button>
        );
      })}
    </div>
  );
  const head = <div className="gt-dialog__head"><h3>{title}</h3><button type="button" onClick={onClose} aria-label="Close"><Icon path={mdiClose} size="22px" /></button></div>;
  if (mobile) {
    return (
      <Drawer open={open} onClose={onClose} placement="bottom" height="85%" closable={false} title={head} rootClassName="gt-sheet-drawer is-tall" destroyOnClose
        footer={<Button type="primary" block size="large" disabled={!draft} onClick={() => { onChange(draft); onClose(); }}>Done</Button>}>
        {list}
      </Drawer>
    );
  }
  return (
    <Modal open={open} onCancel={onClose} footer={null} centered closable={false} width={580} wrapClassName="gt-dialog" destroyOnClose>
      {head}
      {list}
    </Modal>
  );
}

const money = (value: number | undefined) => (value === undefined ? "" : currencyDisplay("MYR", value).replace("RM ", "MYR "));

export default function PaymentStep(props: Props) {
  const { landTour, total, address, gateway, bank, summary } = props;
  const isMobile = useBreakpoint();
  const [editingAddress, setEditingAddress] = useState(false);
  const [bankOpen, setBankOpen] = useState(false);

  const bankOptions = useMemo(() => {
    const channel = PAYMENT_CHANNELS.find((item) => item.value === gateway);
    return normaliseBankOptions(channel ? props.paymentMethods[channel.bankName] || [] : []);
  }, [gateway, props.paymentMethods]);

  const addressText = [address.address_1, address.address_2, address.city_name, address.postal_code, address.state, address.country]
    .filter(Boolean).join(", ");

  const [addressForm] = Form.useForm<InvoiceAddress>();
  const [cities, setCities] = useState<{ value: string; label: string }[]>([]);
  const editedState = Form.useWatch("state", addressForm);

  // Cities follow the chosen state, from the same API the Series booking form uses.
  useEffect(() => {
    if (!editingAddress || !editedState) { setCities([]); return undefined; }
    let active = true;
    new ListApi().getCities({ state: editedState }).then((list) => { if (active) setCities(list); }).catch(() => { if (active) setCities([]); });
    return () => { active = false; };
  }, [editedState, editingAddress]);

  const openAddress = () => { addressForm.setFieldsValue(address); setEditingAddress(true); };
  const closeAddress = () => setEditingAddress(false);
  const saveAddress = async () => {
    try {
      await addressForm.validateFields();
      props.onAddressChange({ ...emptyAddressValues, ...addressForm.getFieldsValue() });
      setEditingAddress(false);
    } catch { /* the form shows what is missing */ }
  };

  // Same all-or-nothing rule as the Series e-invoice address: once any field is filled, every field is needed.
  const requiredWithOthers = (key: keyof InvoiceAddress) => ({ getFieldValue }) => ({
    validator(_: unknown, value: string) {
      const others = (Object.keys(emptyAddressValues) as (keyof InvoiceAddress)[]).some((other) => other !== key && other !== "address_2" && !!getFieldValue(other)?.toString().trim());
      return others && !value?.toString().trim() ? Promise.reject(new Error("This field cannot be empty")) : Promise.resolve();
    },
  });

  // The e-invoice country is Malaysia only, as in Series; states and cities come from the same APIs.
  const countryOptions = [{ value: "Malaysia", label: "Malaysia" }];

  const addressFormNode = (
    <Form form={addressForm} layout="vertical" initialValues={address}>
      {isMobile && <p className="gt-m-sheet-note">Please fill this in if you require an e-Invoice.</p>}
      {ADDRESS_FIELDS.map(({ key, label }) => (
        <Form.Item key={key} label={label} name={key} dependencies={(Object.keys(emptyAddressValues) as string[]).filter((other) => other !== key && other !== "address_2")} rules={key === "address_2" ? [] : [requiredWithOthers(key)]}>
          {key === "state" ? (
            <Select showSearch allowClear placeholder="Please Select" options={props.states} optionFilterProp="label" onChange={() => addressForm.setFieldsValue({ city_name: "" })} />
          ) : key === "city_name" ? (
            <Select showSearch allowClear placeholder="Please Select" options={cities} optionFilterProp="label" disabled={!editedState} />
          ) : key === "country" ? (
            <Select showSearch allowClear placeholder="Please Select" options={countryOptions} optionFilterProp="label" />
          ) : (
            <Input
              placeholder={key === "address_1" ? "e.g. No 12, Jalan Ampang" : key === "address_2" ? "Apartment, suite, unit, block, floor, taman, kampung, etc. (Optional)" : key === "postal_code" ? "e.g. 55100" : undefined}
            />
          )}
        </Form.Item>
      ))}
    </Form>
  );

  const addressEditor = isMobile ? (
    <Drawer
      open={editingAddress}
      onClose={closeAddress}
      width="100%"
      closable={false}
      destroyOnClose
      rootClassName="gt-passenger-drawer gt-m-sheet"
      title={<div className="gt-drawer__head"><span>e-Invoice Address</span><button type="button" onClick={closeAddress} aria-label="Close"><Icon path={mdiClose} size="22px" /></button></div>}
      footer={<div className="gt-drawer__footer is-mobile"><button type="button" className="gt-m-primary" onClick={saveAddress}>Done</button></div>}
    >
      {addressFormNode}
    </Drawer>
  ) : (
    <Modal open={editingAddress} title="e-Invoice Address" centered onCancel={closeAddress} onOk={saveAddress} okText="Done" destroyOnClose>
      {addressFormNode}
    </Modal>
  );

  const invoiceCard = (
    <>
      {!isMobile && <h3 className="gt-card__title">e-Invoice Address</h3>}
      <div className={`gt-inline-box ${isMobile ? "is-card" : ""}`}>
        {isMobile && <h3 className="gt-card__title">e-Invoice Address</h3>}
        <span className={addressText ? "" : "gt-muted"}>{addressText || "Please fill this in if you require an e-Invoice."}</span>
        <button type="button" className="gt-edit-btn" onClick={openAddress}>Edit</button>
      </div>
    </>
  );

  const termsBlock = (
    <div className="gt-terms">
      <div className="gt-terms__option">
        {isMobile && <Icon path={mdiCheckCircle} size="28px" color="#dc1a22" className="gt-terms__check" />}
        <div>
          <strong>Pay in Full</strong>
          <span>One-time complete payment, nothing more to pay</span>
          <b>{money(total)}</b>
        </div>
      </div>
      {!landTour.incInstantConfirmation && (
        <p className="gt-terms__note">
          Your payment will be held for up to <b>72 hours</b> while we confirm hotel and ground-service availability.
          If we are unable to confirm your booking, it will be cancelled and your payment will be refunded.
        </p>
      )}
    </div>
  );

  const methodsBlock = (
    <div className="gt-methods" role="radiogroup" aria-label="Payment method">
      {PAYMENT_CHANNELS.map((channel) => {
        const checked = gateway === channel.value;
        return (
          <div key={channel.id} className={`gt-method ${checked ? "is-selected" : ""}`}>
            <button type="button" role="radio" aria-checked={checked} className="gt-method__row" onClick={() => props.onGatewayChange(channel.value)}>
              <i className="gt-method__dot" />
              <span>{channel.label}</span>
              <img src={`/iceholidays-assets/images/payment/${channel.logo}`} alt="" />
            </button>
            {checked && bankOptions.length > 0 && (
              <>
                <button type="button" className="gt-bank-field" onClick={() => setBankOpen(true)}>
                  <span className={bank ? "" : "is-placeholder"}>{bankOptions.find((option: BankOption) => option.value === bank)?.label || `Select ${channel.fieldLabel.toLowerCase()}`}</span>
                  <Icon path={mdiChevronDown} size="22px" />
                </button>
                <BankChooser
                  open={bankOpen}
                  title={`Choose your ${channel.fieldLabel.toLowerCase()}`}
                  options={bankOptions}
                  value={bank}
                  mobile={isMobile}
                  onClose={() => setBankOpen(false)}
                  onChange={props.onBankChange}
                />
              </>
            )}
          </div>
        );
      })}
    </div>
  );

  const agreement = (
    <Form className="gt-agreement">
      <TermsConsentCheckBox checked={props.termsAgreed} onCheckedChange={props.onTermsChange} />
      <PrivacyConsentCheckbox checked={props.privacyAgreed} onCheckedChange={props.onPrivacyChange} />
    </Form>
  );

  if (isMobile) {
    const iconRow = (icon: string, label: string, value?: string) => value ? (
      <div className="gt-m-sum__row" key={label}>
        <Icon path={icon} size="24px" />
        <div><span>{label}</span><strong>{value}</strong></div>
      </div>
    ) : null;
    return (
      <>
        {summary && (
          <section className="gt-m-sum">
            <div className="gt-m-sum__head">
              <h3>{landTour.title}</h3>
              <button type="button" className="gt-edit-btn" onClick={summary.onEdit}>Edit</button>
            </div>
            {iconRow(mdiCalendarMonth, "Departure Date", dayjs(summary.departureDate).format("DD/MM/YYYY"))}
            {iconRow(mdiAccount, "Traveller Details", summary.travellers)}
            {iconRow(mdiAccount, "Preferred Partner", summary.partner)}
            {iconRow(mdiBedDouble, "Room Selection", summary.room)}
            {iconRow(mdiTranslate, "Language", summary.language)}
            {(
              <div className="gt-m-sum__flights">
                <span>Flight Details</span>
                {(
                  <div><b><Icon path={mdiAirplaneTakeoff} size="20px" />Outbound · {summary.outbound.date ? dayjs(summary.outbound.date).format("DD MMM YYYY") : "Date to be confirmed"}</b><strong>{summary.outbound.line}</strong></div>
                )}
                {(
                  <div><b><Icon path={mdiAirplaneLanding} size="20px" />Return · {summary.inbound.date ? dayjs(summary.inbound.date).format("DD MMM YYYY") : "Date to be confirmed"}</b><strong>{summary.inbound.line}</strong></div>
                )}
              </div>
            )}
          </section>
        )}

        {invoiceCard}

        <h3 className="gt-m-heading">Payment Terms</h3>
        {termsBlock}
        <h3 className="gt-m-heading">Select Payment Method</h3>
        {methodsBlock}
        <h3 className="gt-m-heading">Agreement</h3>
        {agreement}
        {addressEditor}
      </>
    );
  }

  return (
    <>
      <section className="gt-card">
        {invoiceCard}

        <h3 className="gt-card__title gt-spaced">Payment Terms</h3>
        {termsBlock}

        <h3 className="gt-card__title gt-spaced">Select Payment Method</h3>
        {methodsBlock}

        <h3 className="gt-card__title gt-spaced">Agreement</h3>
        {agreement}
      </section>

      <section className="gt-card gt-total-card">
        <div><h3>Total</h3><small>(incl. taxes & fees)</small></div>
        <strong>{money(total)}</strong>
      </section>

      {addressEditor}
    </>
  );
}
